import ExcelJS from 'exceljs';
import DeviceMultiSelector from 'features/Integrations/features/ConnectedDeviceGroups/features/AddEditGroupModal/components/DeviceMultiSelector/DeviceMultiSelector';
import { useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { CUSTOM_URL, TEAM_PREFIX } from 'services/_infra/environment';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { stageFloodSensorData } from '../../../utils/deviceFields.utils';
import {
  buildExportHeaders,
  buildExportRows,
  collectExportFields,
  normalizeExportData,
} from '../../../utils/exportData.utils';
import { fetchAllNodeDataPages } from '../../../utils/exportPagination.utils';

const HEADER_MAP = stageFloodSensorData;

const ExportModal = ({
  startTime,
  setStartTime,
  endTime,
  setEndTime,
  formatDateTimeLocal,
  setShowExportModal,
}) => {
  const [exportFormat, setExportFormat] = useState('csv');
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [customTimeInputs, setCustomTimeImputs] = useState({});
  const [exportingString, setExportingString] = useState('');
  const [includeImages, setIncludeImages] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  // The export is an async multi-page walk, so a second click can land before the
  // disabled button re-renders: the ref is the synchronous guard, the state only
  // drives what the operator sees.
  const isExportingRef = useRef(false);

  const { data: connectedDevices = [], isError: connectedDevicesError } =
    useGetConnectedDevicesQuery();

  const availableFields = useMemo(() => {
    const scopedDevices = selectedDevices.length
      ? connectedDevices.filter((device) => selectedDevices.includes(device.id))
      : connectedDevices;
    return collectExportFields(scopedDevices);
  }, [connectedDevices, selectedDevices]);

  const toggleAllFields = (checked) => {
    setSelectedFields(checked ? availableFields : []);
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportCsv = (rows, headerFields) => {
    const headers = headerFields.map((field) => `"${HEADER_MAP[field] || field}"`).join(',');
    const csvBody = rows
      .map((row) =>
        headerFields.map((field) => `"${String(row[field] ?? '').replace(/"/g, '""')}"`).join(','),
      )
      .join('\n');

    downloadBlob(new Blob([`${headers}\n${csvBody}`], { type: 'text/csv' }), 'export.csv');
  };

  const exportXlsx = async (rows, headerFields) => {
    const headers = includeImages ? [...headerFields, 'image'] : headerFields;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    worksheet.addRow(headers.map((header) => HEADER_MAP[header] || header));
    rows.forEach((row) => {
      worksheet.addRow(headers.map((header) => (header === 'image' ? null : row[header])));
    });

    worksheet.eachRow((row, rowNumber) => {
      if (includeImages && rowNumber !== 1) row.height = 160;
      row.eachCell((cell) => {
        cell.alignment = { vertical: 'top', wrapText: true };
      });
    });

    worksheet.columns.forEach((column) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const length = cell.value ? cell.value.toString().length : 10;
        if (length > maxLength) maxLength = length;
      });
      column.width = maxLength + 2;
    });

    if (includeImages) {
      const imageFetchMap = new Map();
      let processedCount = 0;

      const imageBuffers = await Promise.all(
        rows.map(async (row) => {
          const imageUrl = row.img_link_url;
          if (!imageUrl) return null;

          if (!imageFetchMap.has(imageUrl)) {
            const localImageUrl = `${CUSTOM_URL}${TEAM_PREFIX}/fusion/serve-image?url=${encodeURIComponent(imageUrl)}`;
            imageFetchMap.set(
              imageUrl,
              fetch(localImageUrl).then((response) => {
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                return response.arrayBuffer();
              }),
            );
          }

          const buffer = await imageFetchMap.get(imageUrl);
          processedCount += 1;
          setExportingString(`Adding images: ${processedCount}/${rows.length}`);
          return buffer;
        }),
      );

      setExportingString('Building file...');
      imageBuffers.forEach((buffer, index) => {
        if (!buffer) return;
        const imageId = workbook.addImage({ buffer, extension: 'jpeg' });
        worksheet.addImage(imageId, {
          tl: { col: headers.indexOf('image'), row: index + 1 },
          ext: { width: 64 * 4, height: 48 * 4 },
        });
      });
    }

    setExportingString('Downloading...');
    const buffer = await workbook.xlsx.writeBuffer();
    downloadBlob(
      new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
      'export.xlsx',
    );
  };

  /**
   * The paged walk stopped early because the next request would pass the backend's
   * maximum offset, which it answers with a 400. The file is still written, but it
   * holds only the newest part of the window, so say so: a bounded export otherwise
   * reads as a complete one. One toast id, because >25 devices walk several chunks.
   */
  const handleCapReached = ({ offset }) => {
    const newestPoints = Number.isFinite(offset) && offset > 0 ? offset.toLocaleString() : null;
    const bound = newestPoints
      ? `about the newest ${newestPoints} readings`
      : 'the newest readings';
    toast.warn(
      `This export reached the server's data limit, so it covers ${bound} per device. Narrow the date range to export the older data.`,
      { toastId: 'device-export-capped' },
    );
  };

  const handleExport = async () => {
    // A second walk would double the request fan-out and race the first one's progress
    // text and download, so one click owns the export until it settles.
    if (isExportingRef.current) return;

    if (selectedDevices.length === 0) {
      toast.error('Please select at least one device to export.');
      return;
    }

    isExportingRef.current = true;
    setIsExporting(true);
    setExportingString('Fetching data...');

    try {
      // The endpoint answers one page of at most 5000 points per device for at most 25
      // devices, so the helper pages every chunk until `page.hasMore` is false; a failed
      // page throws, so the export below only ever runs on complete data.
      const result = await fetchAllNodeDataPages({
        startTime,
        endTime,
        ids: selectedDevices,
        onProgress: ({ rows }) => setExportingString(`Fetching data... ${rows} rows`),
        onCapReached: handleCapReached,
      });

      const dataPointsByDeviceId = new Map();
      normalizeExportData(result).forEach(({ deviceId, points }) => {
        dataPointsByDeviceId.set(deviceId, [
          ...(dataPointsByDeviceId.get(deviceId) || []),
          ...points,
        ]);
      });

      const rows = buildExportRows(
        selectedDevices,
        connectedDevices,
        dataPointsByDeviceId,
        selectedFields,
      );

      if (rows.length === 0) {
        setExportingString('');
        toast.error('No data available for the selected devices and timeframe.');
        return;
      }

      const headerFields = buildExportHeaders(selectedFields);

      if (exportFormat === 'csv') {
        exportCsv(rows, headerFields);
      } else {
        await exportXlsx(rows, headerFields);
      }

      setExportingString('Download complete.');
      toast.success('Export complete.');
      setTimeout(() => setExportingString(''), 5000);
    } catch (error) {
      console.error('Device export failed:', error);
      setExportingString('');
      toast.error('Export failed. Please try again.');
    } finally {
      isExportingRef.current = false;
      setIsExporting(false);
    }
  };

  if (connectedDevicesError) {
    return <div className="text-danger">Unable to load devices.</div>;
  }

  return (
    <div className="d-flex flex-column gap-2">
      <div className="d-flex flex-column flex-lg-row gap-2">
        <div className="d-flex flex-column flex-fill flex-basis-0">
          <div className="d-flex flex-column flex-lg-row align-items-baseline">
            <label className="flex-fill flex-basis-0">Start Date</label>
            <input
              className="form-control flex-fill flex-basis-0"
              type="datetime-local"
              value={customTimeInputs.start || formatDateTimeLocal(startTime)}
              onChange={(e) => {
                setCustomTimeImputs({
                  ...customTimeInputs,
                  start: e.target.value,
                });
                setStartTime(new Date(e.target.value).getTime());
              }}
            />
          </div>
        </div>

        <div className="d-flex flex-column flex-fill flex-basis-0">
          <div className="d-flex flex-column flex-lg-row align-items-baseline">
            <label className="flex-fill flex-basis-0">End Date</label>
            <input
              className="form-control flex-fill flex-basis-0"
              type="datetime-local"
              value={customTimeInputs.end || formatDateTimeLocal(endTime)}
              onChange={(e) => {
                setCustomTimeImputs({
                  ...customTimeInputs,
                  end: e.target.value,
                });

                setEndTime(new Date(e.target.value).getTime());
              }}
            />
          </div>
        </div>
      </div>

      <hr className="d-block d-lg-none" />

      <div className="d-flex flex-column flex-lg-row gap-2">
        <div className="flex-fill flex-basis-0">
          <div>
            <p className="h5">Select Device(s)</p>
            <div className="d-flex gap-2">
              <Form.Check
                type="checkbox"
                checked={
                  connectedDevices.length > 0 && selectedDevices.length === connectedDevices.length
                }
                onChange={(e) =>
                  setSelectedDevices(
                    e.target.checked ? connectedDevices.map((device) => device.id) : [],
                  )
                }
                label="Select All"
              />
            </div>
          </div>

          <div className="position-relative min-h-250 bg-primary-shadow">
            <div className="position-absolute-fill overflow-x-hidden overflow-y-auto p-2">
              <DeviceMultiSelector
                value={selectedDevices}
                enforceTypeRules={false}
                onChange={(deviceIds) => setSelectedDevices(deviceIds)}
              />
            </div>
          </div>
        </div>

        <div className="flex-fill flex-basis-0">
          <div>
            <p className="h5">Data to Export</p>
            <div className="d-flex gap-2">
              <Form.Check
                type="checkbox"
                checked={
                  availableFields.length > 0 && selectedFields.length === availableFields.length
                }
                onChange={(e) => toggleAllFields(e.target.checked)}
                label="Select All"
              />
            </div>
          </div>

          <div className="position-relative min-h-250 bg-primary-shadow">
            <div className="position-absolute-fill d-flex flex-column overflow-x-hidden overflow-y-auto gap-2 p-2">
              {availableFields.length === 0 && (
                <div className="text-secondary">
                  Select a device with history to see available fields.
                </div>
              )}
              {[...availableFields]
                .sort((a, b) => ((HEADER_MAP[a] || a) > (HEADER_MAP[b] || b) ? 1 : -1))
                .map((field) => (
                  <Form.Check
                    key={field}
                    type="checkbox"
                    checked={selectedFields.includes(field)}
                    onChange={() => {
                      setSelectedFields((current) =>
                        current.includes(field)
                          ? current.filter((f) => f !== field)
                          : [...current, field],
                      );
                    }}
                    label={HEADER_MAP[field] || field}
                  />
                ))}
            </div>
          </div>
        </div>
      </div>

      <hr className="d-block d-lg-none" />

      <div className="d-flex flex-column flex-lg-row justify-content-between">
        <div>
          <div className="h5">Export Document Format</div>
          <div className="d-flex gap-4">
            <Form.Check
              type="checkbox"
              checked={exportFormat === 'csv'}
              onChange={() => setExportFormat('csv')}
              label="CSV"
            />
            <Form.Check
              type="checkbox"
              checked={exportFormat === 'xls'}
              onChange={() => setExportFormat('xls')}
              label="Excel"
            />
          </div>
        </div>

        <div className="d-flex gap-2 justify-content-between align-items-baseline">
          <div>
            {exportFormat === 'xls' && (
              <Form.Check
                type="checkbox"
                checked={includeImages}
                onChange={() => setIncludeImages(!includeImages)}
                label="Include Images (Larger file)"
              />
            )}
            {/* The only feedback for a walk that can run for minutes, so announce it. */}
            <div role="status" aria-live="polite">
              {exportingString || ' '}
            </div>
          </div>
          <button type="button" className="btn" onClick={() => setShowExportModal(false)}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleExport}
            disabled={isExporting}
            aria-busy={isExporting}
          >
            <p className="m-0 fw-bold py-2">Export</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
